import { CurrencyPipe, formatDate } from "@angular/common";
import { inject, Injectable, signal } from '@angular/core';
import { AnnualStatementPageFilter, SigningStatus } from "@models/annual-statements/annual-statement-enums";
import { AnnualStatementRecord } from "@models/annual-statements/annual-statement-record";
import { Tag } from "@models/common/tag";
import { ColumnWithExportName } from "@models/grid/column-with-export-name";
import { DocumentToAttach } from "@models/ui-models/files";
import { ExcelRow, ExcelStyle, ProcessCellForExportParams, ProcessHeaderForExportParams } from "ag-grid-community";
import { ACNPipe } from "../pipes/acnPipe";
import { excelReportTitleStyles, excelTitleForReport } from "../utils/excel-title-for-report";

@Injectable({
  providedIn: 'root'
})
export class AnnualStatementsService {
  private currencyPipe = inject(CurrencyPipe);

  patchLoading = signal(false);
  sendSignLoading= signal(false);
  disabledHeaderBtn = signal(false);
  showVoidEnvelope = signal(false);
  generateStatementLoading = signal(false);
  showSendEmailBtn= signal(false);
  showSendPaperBtn = signal(false);
  documentsToAttach = signal<DocumentToAttach[]>([]);
  pageFilter = signal<AnnualStatementPageFilter>(0);
  numberColumnForExport = signal(0);

  public exportParams() {
    const currencyPipe = this.currencyPipe;

    return {
      processCellCallback(params: ProcessCellForExportParams): string {
        const value: unknown = params.value;
        const annualData = params.node?.data as AnnualStatementRecord;

        if (params.column.getColId() === 'companyName') {
          return annualData.companyName;
        } if (params.column.getColId() === 'acn') {
          return ACNPipe.formatValue(annualData.acn);
        } if (params.column.getColId() === 'status') {
          return AnnualStatementPageFilter[annualData.status];
        } if (params.column.getColId() === 'reviewDate') {
          return annualData.reviewDate  == null ? '' : formatDate(annualData.reviewDate, 'dd MMM yyyy', 'en-US');
        } if (params.column.getColId() === 'paymentDeadline') {
          return annualData.paymentDeadline  == null ? '' : formatDate(annualData.paymentDeadline, 'dd MMM yyyy', 'en-US');
        } if (params.column.getColId() === 'debt') {
          return currencyPipe.transform(annualData.debt,'$','code','1.2-2') ?? '';
        } else if (params.column.getColId() === 'tags') {
          return (value as Tag[])?.map((tag: Tag) => tag.name)?.join(', ');
        } else if (params.column.getColId() === 'signingStatus') {
          return SigningStatus[annualData.signingStatus];
        }

        return value as string;
      },
      processHeaderCallback(params: ProcessHeaderForExportParams) {
        const colDef = params.column.getColDef() as ColumnWithExportName;
        return colDef?.exportColumnName ?? colDef.headerName;
      },
      sheetName: 'Annual statements',
    }
  }

  exportParamsXls() {
    return {
      ...this.exportParams(),
      prependContent: this.getExcelTitleRows(this.numberColumnForExport())
    };
  }

  public getExcelTitleRows: (numberColumnForExport: number) => ExcelRow[] = (numberColumnForExport) =>
    excelTitleForReport(numberColumnForExport, 'Annual Statements Report');

  public readonly excelStyles: ExcelStyle[] = excelReportTitleStyles;
}
